import { Injectable, effect, inject, signal } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, tap, switchMap, catchError, of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  GeneralData,
  GeneralDataResponse,
  OrganizationContact,
  FattureElettronicheXml,
  FattureCartaceePdf,
  ScrittureLibriContabili,
  AltriDocumentiRilevanzaContabile,
  DichiarazioniComunicazioniFisco,
  MessaggiPec,
  LibroUnicoLavoro,
  AltreTipologieDocumentali,
  RentriData,
  OrganizationStatus,
  OrganizationListResponse,
  VerifyDataRequest,
  VerifyDataResponse,
  VerifyTipoRequest,
  VerifyEmailRequest,
  VerifyEmailResponse,
  DocumentComparison,
  TipoDocumentComparisonResponse,
  AcceptResponse,
} from '../../../core/models/organization.models';
import { AuthService } from '../../../core/service/auth.service';
import { ErrorMapperService } from '../../../core/service/error-mapper.service';
import { ToastService } from '../../ui/toast/toast.service';
import { CONTENT_LOADING } from '../../../core/interceptors/loading.interceptor';
import { SKIP_ERROR_TOAST } from '../../../core/interceptors/error.interceptor';
import { ValidationEmailMessage } from '../../models/validation-dialog.models';
import { ConservatoreNotAcceptedDetail } from '../../../core/models/error.models';
import {
  ORGANIZATION_ENDPOINTS,
  type OrganizationEndpointKey,
} from '../../constants/organization-endpoints.constants';

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly errorMapper = inject(ErrorMapperService);
  private readonly API_URL = environment.apiBaseUrl;
  private previousOrganizationId: number | null = null;

  private readonly _status = signal<OrganizationStatus | null>(null);
  readonly status = this._status.asReadonly();

  private readonly _conservatoreNotAcceptedError = signal<ConservatoreNotAcceptedDetail | null>(
    null,
  );
  readonly conservatoreNotAcceptedError = this._conservatoreNotAcceptedError.asReadonly();

  constructor() {
    effect(() => {
      const currentOrganizationId = this.authService.activeOrganizationId();

      if (this.previousOrganizationId === null) {
        this.previousOrganizationId = currentOrganizationId;
        return;
      }

      if (this.previousOrganizationId !== currentOrganizationId) {
        this._status.set(null);
        this.previousOrganizationId = currentOrganizationId;
      }
    });
  }

  private get organizationId(): number | null {
    return this.authService.activeOrganizationId();
  }

  /**
   * Builds the full API URL for a given endpoint.
   */
  private buildUrl(endpoint: OrganizationEndpointKey): string {
    const path = ORGANIZATION_ENDPOINTS[endpoint];
    if (path.startsWith('/api/')) {
      return `${this.API_URL}${path}`;
    }
    return `${this.API_URL}/api/organizations/${this.organizationId}/${path}`;
  }

  /**
   * Validates that organizationId exists, throws if not.
   */
  private validateOrgId(): Observable<never> | null {
    if (!this.organizationId) {
      return throwError(() => new Error('Organization ID not found'));
    }
    return null;
  }

  /**
   * Generic GET request with content loading indicator and status refresh.
   */
  private get<T>(endpoint: OrganizationEndpointKey): Observable<T> {
    const error = this.validateOrgId();
    if (error) return error;

    return this.http
      .get<T>(this.buildUrl(endpoint), {
        context: new HttpContext().set(CONTENT_LOADING, true),
      })
      .pipe(this.withStatusRefresh());
  }

  /**
   * Generic PUT request with content loading indicator and status refresh.
   */
  private put<T, R = T>(endpoint: OrganizationEndpointKey, data: T): Observable<R> {
    const error = this.validateOrgId();
    if (error) return error;

    return this.http
      .put<R>(this.buildUrl(endpoint), data, {
        context: new HttpContext().set(CONTENT_LOADING, true),
      })
      .pipe(this.withStatusRefresh());
  }

  /**
   * Generic POST request with content loading indicator and status refresh.
   */
  private post<T, R = T>(endpoint: OrganizationEndpointKey, data: T): Observable<R> {
    const error = this.validateOrgId();
    if (error) return error;

    return this.http
      .post<R>(this.buildUrl(endpoint), data, {
        context: new HttpContext().set(CONTENT_LOADING, true),
      })
      .pipe(this.withStatusRefresh());
  }

  /**
   * Refreshes status after successful operations using proper RxJS chaining.
   * Use switchMap instead of nested subscribe to prevent memory leaks.
   */
  private withStatusRefresh<T>(): (source: Observable<T>) => Observable<T> {
    return (source: Observable<T>) =>
      source.pipe(
        switchMap((result) =>
          this.getStatus(true).pipe(
            catchError((err) => {
              console.error('Error refreshing status:', err);
              return of(null);
            }),
            // Return the original result, not the status
            switchMap(() => of(result)),
          ),
        ),
      );
  }

  private isConservatoreNotAcceptedError(err: HttpErrorResponse): boolean {
    if (err.status !== 422) return false;
    const detail = err.error?.detail;
    return (
      typeof detail === 'object' &&
      detail !== null &&
      typeof detail.message === 'string' &&
      detail.message.startsWith('Cannot accept section')
    );
  }

  private handleUpdateStatusError(err: HttpErrorResponse): Observable<never> {
    if (this.isConservatoreNotAcceptedError(err)) {
      this._conservatoreNotAcceptedError.set(err.error.detail as ConservatoreNotAcceptedDetail);
    } else {
      this.toast.error(this.errorMapper.mapError(err.error));
    }
    return throwError(() => err);
  }

  dismissConservatoreNotAcceptedError(): void {
    this._conservatoreNotAcceptedError.set(null);
  }

  getStatus(skipErrorToast = false): Observable<OrganizationStatus> {
    const error = this.validateOrgId();
    if (error) return error;

    const context = new HttpContext().set(CONTENT_LOADING, true);
    if (skipErrorToast) {
      context.set(SKIP_ERROR_TOAST, true);
    }

    return this.http
      .get<OrganizationStatus>(this.buildUrl('status'), {
        context,
      })
      .pipe(tap((status) => this._status.set(status)));
  }

  // ==================== General Data ====================

  getGeneralData(): Observable<GeneralDataResponse> {
    return this.get<GeneralDataResponse>('generalData');
  }

  updateGeneralData(data: GeneralData): Observable<GeneralDataResponse> {
    return this.put<GeneralData, GeneralDataResponse>('generalData', data);
  }

  // ==================== Contacts ====================

  getContact(): Observable<OrganizationContact> {
    return this.get<OrganizationContact>('contacts');
  }

  updateContact(data: OrganizationContact): Observable<OrganizationContact> {
    return this.put<OrganizationContact>('contacts', data);
  }

  // ==================== Fatture Elettroniche XML ====================

  getFattureElettronicheXml(): Observable<FattureElettronicheXml> {
    return this.get<FattureElettronicheXml>('fattureElettronicheXml');
  }

  updateFattureElettronicheXml(data: FattureElettronicheXml): Observable<FattureElettronicheXml> {
    return this.put<FattureElettronicheXml>('fattureElettronicheXml', data);
  }

  // ==================== Fatture Cartacee PDF ====================

  getFattureCartaceePdf(): Observable<FattureCartaceePdf> {
    return this.get<FattureCartaceePdf>('fattureCartaceePdf');
  }

  updateFattureCartaceePdf(data: FattureCartaceePdf): Observable<FattureCartaceePdf> {
    return this.put<FattureCartaceePdf>('fattureCartaceePdf', data);
  }

  // ==================== Scritture Libri Contabili ====================

  getScrittureLibriContabili(): Observable<ScrittureLibriContabili> {
    return this.get<ScrittureLibriContabili>('scrittureLibriContabili');
  }

  updateScrittureLibriContabili(
    data: ScrittureLibriContabili,
  ): Observable<ScrittureLibriContabili> {
    return this.put<ScrittureLibriContabili>('scrittureLibriContabili', data);
  }

  // ==================== Altri Documenti Rilevanza Contabile ====================

  getAltriDocumentiRilevanzaContabile(): Observable<AltriDocumentiRilevanzaContabile> {
    return this.get<AltriDocumentiRilevanzaContabile>('altriDocumentiRilevanzaContabile');
  }

  updateAltriDocumentiRilevanzaContabile(
    data: AltriDocumentiRilevanzaContabile,
  ): Observable<AltriDocumentiRilevanzaContabile> {
    return this.put<AltriDocumentiRilevanzaContabile>('altriDocumentiRilevanzaContabile', data);
  }

  // ==================== Dichiarazioni Comunicazioni Fisco ====================

  getDichiarazioniComunicazioniFisco(): Observable<DichiarazioniComunicazioniFisco> {
    return this.get<DichiarazioniComunicazioniFisco>('dichiarazioniComunicazioniFisco');
  }

  updateDichiarazioniComunicazioniFisco(
    data: DichiarazioniComunicazioniFisco,
  ): Observable<DichiarazioniComunicazioniFisco> {
    return this.put<DichiarazioniComunicazioniFisco>('dichiarazioniComunicazioniFisco', data);
  }

  // ==================== Messaggi PEC ====================

  getMessaggiPec(): Observable<MessaggiPec> {
    return this.get<MessaggiPec>('messaggiPec');
  }

  updateMessaggiPec(data: MessaggiPec): Observable<MessaggiPec> {
    return this.put<MessaggiPec>('messaggiPec', data);
  }

  // ==================== Libro Unico Lavoro ====================

  getLibroUnicoLavoro(): Observable<LibroUnicoLavoro> {
    return this.get<LibroUnicoLavoro>('libroUnicoLavoro');
  }

  updateLibroUnicoLavoro(data: LibroUnicoLavoro): Observable<LibroUnicoLavoro> {
    return this.put<LibroUnicoLavoro>('libroUnicoLavoro', data);
  }

  // ==================== Altre Tipologie Documentali ====================

  getAltreTipologieDocumentali(): Observable<AltreTipologieDocumentali> {
    return this.get<AltreTipologieDocumentali>('altreTipologieDocumentali');
  }

  updateAltreTipologieDocumentali(
    data: AltreTipologieDocumentali,
  ): Observable<AltreTipologieDocumentali> {
    return this.put<AltreTipologieDocumentali>('altreTipologieDocumentali', data);
  }

  // ==================== RENTRI ====================

  getRentri(): Observable<RentriData> {
    return this.get<RentriData>('rentri');
  }

  updateRentri(data: RentriData): Observable<RentriData> {
    return this.put<RentriData>('rentri', data);
  }

  // ==================== Notifications ====================

  notify(section: string): Observable<void> {
    return this.post<{ section: string }, void>('notify', { section });
  }

  sentForReview(section: string): Observable<void> {
    const error = this.validateOrgId();
    if (error) return error;

    return this.http
      .post<void>(
        `${this.API_URL}/api/organizations/${this.organizationId}/${section}/send-for-review`,
        {},
        { context: new HttpContext().set(CONTENT_LOADING, true) },
      )
      .pipe(this.withStatusRefresh());
  }

  // ==================== Section-Level Status ====================

  updateSectionStatus<T>(section: string, newStatus: string, reason?: string): Observable<T> {
    const error = this.validateOrgId();
    if (error) return error;

    const payload: Record<string, unknown> = { new_status: newStatus };
    if (reason !== undefined) {
      payload['rejection_reason'] = reason;
    }

    return this.http
      .patch<T>(
        `${this.API_URL}/api/organizations/${this.organizationId}/${section}/update-status`,
        payload,
        { context: new HttpContext().set(CONTENT_LOADING, true).set(SKIP_ERROR_TOAST, true) },
      )
      .pipe(
        catchError((err: HttpErrorResponse) => this.handleUpdateStatusError(err)),
        this.withStatusRefresh(),
      );
  }

  // ==================== Per-Tipo Actions ====================

  sendTipoForReview<T>(sectionSlug: string, tipoId: number): Observable<T> {
    const error = this.validateOrgId();
    if (error) return error;

    return this.http
      .post<T>(
        `${this.API_URL}/api/organizations/${this.organizationId}/${sectionSlug}/send-for-review`,
        { tipo_id: tipoId },
        { context: new HttpContext().set(CONTENT_LOADING, true) },
      )
      .pipe(this.withStatusRefresh());
  }

  deleteTipo<T>(sectionSlug: string, tipi: string[], ultimoAnno?: number): Observable<T> {
    const error = this.validateOrgId();
    if (error) return error;

    const body: Record<string, unknown> = { tipo: tipi };
    if (ultimoAnno !== undefined) {
      body['ultimo_anno_invio_conservazione'] = ultimoAnno;
    }

    return this.http
      .delete<T>(`${this.API_URL}/api/organizations/${this.organizationId}/${sectionSlug}/tipo`, {
        body,
        context: new HttpContext().set(CONTENT_LOADING, true),
      })
      .pipe(this.withStatusRefresh());
  }

  updateTipoStatus<T>(
    sectionSlug: string,
    tipoId: number,
    newStatus: string,
    reason?: string,
  ): Observable<T> {
    const error = this.validateOrgId();
    if (error) return error;

    const payload: Record<string, unknown> = {
      tipo_id: tipoId,
      new_status: newStatus,
    };
    if (reason !== undefined) {
      payload['rejection_reason'] = reason;
    }

    return this.http
      .patch<T>(
        `${this.API_URL}/api/organizations/${this.organizationId}/${sectionSlug}/update-status`,
        payload,
        { context: new HttpContext().set(CONTENT_LOADING, true).set(SKIP_ERROR_TOAST, true) },
      )
      .pipe(
        catchError((err: HttpErrorResponse) => this.handleUpdateStatusError(err)),
        this.withStatusRefresh(),
      );
  }

  // ==================== Accept / Reject ====================

  accept(): Observable<AcceptResponse> {
    return this.post<object, AcceptResponse>('accept', {});
  }

  reject(): Observable<void> {
    return this.post<object, void>('reject', {});
  }

  verifyData(versionId?: number): Observable<VerifyDataResponse> {
    const error = this.validateOrgId();
    if (error) return error;

    const body: VerifyDataRequest = {
      organization_id: this.organizationId!,
    };
    if (versionId != null) {
      body.version_id = versionId;
    }

    return this.post<VerifyDataRequest, VerifyDataResponse>('verifyData', body);
  }

  verifySectionTipo(sectionSlug: string, tipoDocumento: string): Observable<VerifyDataResponse> {
    const error = this.validateOrgId();
    if (error) return error;

    const body: VerifyTipoRequest = { tipo_documento: tipoDocumento };
    return this.http.post<VerifyDataResponse>(
      `${this.API_URL}/api/organizations/${this.organizationId}/${sectionSlug}/verify-tipo`,
      body,
      { context: new HttpContext().set(CONTENT_LOADING, true) },
    );
  }

  verifySection(sectionSlug: string): Observable<VerifyDataResponse> {
    const error = this.validateOrgId();
    if (error) return error;

    return this.http.post<VerifyDataResponse>(
      `${this.API_URL}/api/organizations/${this.organizationId}/${sectionSlug}/verify-tipo`,
      {},
      { context: new HttpContext().set(CONTENT_LOADING, true) },
    );
  }

  sendValidation(
    messages: ValidationEmailMessage[],
    versionId: number,
  ): Observable<VerifyEmailResponse> {
    const error = this.validateOrgId();
    if (error) return error;

    const requestBody: VerifyEmailRequest = {
      organization_id: this.organizationId!,
      version_id: versionId,
      messages: messages.map((message) => ({
        rule_ids: [...message.ruleIds],
        section_doc_type: message.sectionDocType,
        section_doc_subtype: message.sectionDocSubtype,
        subject: message.subject,
        body: message.body,
      })),
    };

    return this.http.post<VerifyEmailResponse>(this.buildUrl('sendEmail'), requestBody, {
      context: new HttpContext().set(CONTENT_LOADING, true),
    });
  }

  compareVersion(currentVersionId: number): Observable<DocumentComparison[]> {
    const orgId = this.organizationId;
    if (!orgId) return throwError(() => new Error('Organization ID not found'));

    return this.http.get<DocumentComparison[]>(
      `${this.API_URL}/api/document_versions/${orgId}/compare/${currentVersionId}`,
      {
        context: new HttpContext().set(CONTENT_LOADING, true),
      },
    );
  }

  compareTipo(
    sectionSlug: string,
    tipoDocumento: string,
  ): Observable<TipoDocumentComparisonResponse> {
    const error = this.validateOrgId();
    if (error) return error;

    return this.http.post<TipoDocumentComparisonResponse>(
      `${this.API_URL}/api/organizations/${this.organizationId}/${sectionSlug}/compare-tipo`,
      {
        tipo_documento: tipoDocumento,
      },
      {
        context: new HttpContext().set(CONTENT_LOADING, true),
      },
    );
  }

  compareSection(sectionSlug: string): Observable<TipoDocumentComparisonResponse> {
    const error = this.validateOrgId();
    if (error) return error;

    return this.http.post<TipoDocumentComparisonResponse>(
      `${this.API_URL}/api/organizations/${this.organizationId}/${sectionSlug}/compare-tipo`,
      {},
      {
        context: new HttpContext().set(CONTENT_LOADING, true),
      },
    );
  }

  // ==================== Admin Methods ====================

  /**
   * Fetches a paginated list of all organizations.
   * This is an admin-only operation.
   */
  getOrganizations(
    skip = 0,
    limit = 20,
    search?: string,
    mainStatus?: string,
    subStatus?: string,
  ): Observable<OrganizationListResponse> {
    const params: Record<string, string> = {
      skip: skip.toString(),
      limit: limit.toString(),
    };

    if (search) {
      params['search'] = search;
    }

    if (mainStatus) {
      params['main_status'] = mainStatus;
    }

    if (subStatus) {
      params['sub_status'] = subStatus;
    }

    return this.http.get<OrganizationListResponse>(`${this.API_URL}/api/organizations/`, {
      params,
      context: new HttpContext().set(CONTENT_LOADING, true),
    });
  }

  // ==================== Doc Subtypes ====================

  getDocSubtypes(docSubtype: string): Observable<string[]> {
    const error = this.validateOrgId();
    if (error) return error;

    return this.http.get<string[]>(
      `${this.API_URL}/api/organizations/${this.organizationId}/doc-subtypes/${docSubtype}`,
      {
        context: new HttpContext().set(CONTENT_LOADING, true),
      },
    );
  }
}
